import { supabase } from './supabase'
import type { Zone, Vendor, Part, WantedOrder, Profile, AdminUser, Brand, Post } from './types'

// -- Profile (own) --

export async function fetchMyProfile(): Promise<Profile | null> {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return null
  const { data, error } = await supabase
    .from('profiles')
    .select('role')
    .eq('user_id', user.id)
    .maybeSingle()
  if (error || !data) return null
  return data as Profile
}

// -- Zones --

export async function fetchZones(): Promise<Zone[]> {
  const { data, error } = await supabase.from('zones').select('*').order('sort_order')
  if (error) throw error
  return (data ?? []) as Zone[]
}

export async function createZone(zone: Partial<Zone>): Promise<void> {
  const { error } = await supabase.from('zones').insert(zone)
  if (error) throw error
}

export async function updateZone(id: string, zone: Partial<Zone>): Promise<void> {
  const { error } = await supabase.from('zones').update(zone).eq('id', id)
  if (error) throw error
}

export async function deleteZone(id: string): Promise<void> {
  const { error } = await supabase.from('zones').delete().eq('id', id)
  if (error) throw error
}

// -- Vendors --

export async function fetchVendors(): Promise<Vendor[]> {
  const { data, error } = await supabase.from('vendors').select('*').order('name')
  if (error) throw error
  return (data ?? []) as Vendor[]
}

export async function fetchVendor(id: string): Promise<Vendor | null> {
  const { data, error } = await supabase.from('vendors').select('*').eq('id', id).maybeSingle()
  if (error) throw error
  return data as Vendor | null
}

export async function createVendor(vendor: Partial<Vendor>): Promise<void> {
  const { error } = await supabase.from('vendors').insert(vendor)
  if (error) throw error
}

export async function updateVendor(id: string, vendor: Partial<Vendor>): Promise<void> {
  const { error } = await supabase.from('vendors').update(vendor).eq('id', id)
  if (error) throw error
}

export async function deleteVendor(id: string): Promise<void> {
  const { error } = await supabase.from('vendors').delete().eq('id', id)
  if (error) throw error
}

// -- Parts --

export async function fetchParts(): Promise<Part[]> {
  const { data, error } = await supabase.from('parts').select('*, vendor:vendors(name, slug)').order('created_at', { ascending: false })
  if (error) throw error
  return (data ?? []) as Part[]
}

export async function fetchPart(id: string): Promise<Part | null> {
  const { data, error } = await supabase.from('parts').select('*').eq('id', id).maybeSingle()
  if (error) throw error
  return data as Part | null
}

export async function createPart(part: Partial<Part>): Promise<void> {
  const { error } = await supabase.from('parts').insert(part)
  if (error) throw error
}

export async function updatePart(id: string, part: Partial<Part>): Promise<void> {
  const { error } = await supabase.from('parts').update(part).eq('id', id)
  if (error) throw error
}

export async function deletePart(id: string): Promise<void> {
  const { error } = await supabase.from('parts').delete().eq('id', id)
  if (error) throw error
}

// -- Wanted orders --

export async function fetchWanted(): Promise<WantedOrder[]> {
  const { data, error } = await supabase.from('wanted_orders').select('*').order('created_at', { ascending: false })
  if (error) throw error
  return (data ?? []) as WantedOrder[]
}

export async function fetchWantedOrder(id: string): Promise<WantedOrder | null> {
  const { data, error } = await supabase.from('wanted_orders').select('*').eq('id', id).maybeSingle()
  if (error) throw error
  return data as WantedOrder | null
}

export async function createWanted(w: Partial<WantedOrder>): Promise<void> {
  const { error } = await supabase.from('wanted_orders').insert(w)
  if (error) throw error
}

export async function updateWanted(id: string, w: Partial<WantedOrder>): Promise<void> {
  const { error } = await supabase.from('wanted_orders').update(w).eq('id', id)
  if (error) throw error
}

export async function deleteWanted(id: string): Promise<void> {
  const { error } = await supabase.from('wanted_orders').delete().eq('id', id)
  if (error) throw error
}

// -- Brands --

export async function fetchBrands(): Promise<Brand[]> {
  const { data, error } = await supabase.from('brands').select('*').order('sort_order')
  if (error) throw error
  return (data ?? []) as Brand[]
}

export async function createBrand(brand: Partial<Brand>): Promise<void> {
  const { error } = await supabase.from('brands').insert(brand)
  if (error) throw error
}

export async function updateBrand(id: string, brand: Partial<Brand>): Promise<void> {
  const { error } = await supabase.from('brands').update(brand).eq('id', id)
  if (error) throw error
}

export async function deleteBrand(id: string): Promise<void> {
  const { error } = await supabase.from('brands').delete().eq('id', id)
  if (error) throw error
}

// -- Blog posts --

export async function fetchPosts(): Promise<Post[]> {
  const { data, error } = await supabase.from('posts').select('*').order('published_at', { ascending: false })
  if (error) throw error
  return (data ?? []) as Post[]
}

export async function fetchPost(id: string): Promise<Post | null> {
  const { data, error } = await supabase.from('posts').select('*').eq('id', id).maybeSingle()
  if (error) throw error
  return data as Post | null
}

export async function createPost(post: Partial<Post>): Promise<void> {
  const { error } = await supabase.from('posts').insert(post)
  if (error) throw error
}

export async function updatePost(id: string, post: Partial<Post>): Promise<void> {
  const { error } = await supabase.from('posts').update(post).eq('id', id)
  if (error) throw error
}

export async function deletePost(id: string): Promise<void> {
  const { error } = await supabase.from('posts').delete().eq('id', id)
  if (error) throw error
}

// -- Users (admin) --

export async function fetchUsers(): Promise<AdminUser[]> {
  const { data, error } = await supabase.rpc('list_users')
  if (error) throw error
  return data ?? []
}

export async function adminUpdateUserRole(userId: string, newRole: string) {
  const { error } = await supabase.rpc('update_user_role', { target_user_id: userId, new_role: newRole })
  if (error) throw error
}

// -- Dashboard stats --

export async function fetchDashboardStats() {
  const [parts, vendors, wanted, zones, brands, posts] = await Promise.all([
    supabase.from('parts').select('id, price_aed, is_active, featured'),
    supabase.from('vendors').select('id, is_active, verified'),
    supabase.from('wanted_orders').select('id, is_active'),
    supabase.from('zones').select('id'),
    supabase.from('brands').select('id'),
    supabase.from('posts').select('id, is_active'),
  ])
  const partsArr = (parts.data ?? []) as { price_aed: number; is_active: boolean; featured: boolean }[]
  const vendorsArr = (vendors.data ?? []) as { is_active: boolean; verified: boolean }[]
  const wantedArr = (wanted.data ?? []) as { is_active: boolean }[]

  return {
    totalParts: partsArr.length,
    activeParts: partsArr.filter(p => p.is_active).length,
    featuredParts: partsArr.filter(p => p.featured).length,
    totalValue: partsArr.reduce((s, p) => s + (Number(p.price_aed) || 0), 0),
    totalVendors: vendorsArr.length,
    verifiedVendors: vendorsArr.filter(v => v.verified).length,
    totalWanted: wantedArr.length,
    activeWanted: wantedArr.filter(w => w.is_active).length,
    totalZones: zones.data?.length ?? 0,
    totalBrands: brands.data?.length ?? 0,
    totalPosts: posts.data?.length ?? 0,
  }
}
